import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, LoaderCircle } from "lucide-react";
import { CONTACT_INFO, INTERIOR_IMAGES } from "../data/site";
import { Reveal, SectionHeading } from "./Reveal";
import { RotatingImage } from "./RotatingImage";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORK_TYPE_VALUES = ["ristrutturazione", "nuova-costruzione", "cappotto", "restauro", "altro"];
const CARD_IMAGES = [INTERIOR_IMAGES.living, INTERIOR_IMAGES.bathroom, INTERIOR_IMAGES.kitchen];

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-brand";

export default function ContactBlock() {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", tipo_lavoro: "ristrutturazione", metratura: "", messaggio: "" });
  const [sending, setSending] = useState(false);
  const { t } = useLang();

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        metratura: form.metratura ? parseInt(form.metratura, 10) : null,
      });
      toast.success(t.contact.toastOk, { description: t.contact.toastOkDesc });
      setForm({ nome: "", email: "", telefono: "", tipo_lavoro: "ristrutturazione", metratura: "", messaggio: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(t.contact.toastErr, {
        description: typeof detail === "string" ? detail : t.contact.toastErrDesc,
      });
    } finally {
      setSending(false);
    }
  };

  const infoItems = [
    { icon: MapPin, label: t.contact.labels.sede, value: CONTACT_INFO.address },
    { icon: Phone, label: t.contact.labels.telefono, value: CONTACT_INFO.phone },
    { icon: Mail, label: t.contact.labels.email, value: CONTACT_INFO.email },
    { icon: Clock, label: t.contact.labels.orari, value: CONTACT_INFO.hours },
  ];

  return (
    <div className="grid gap-10 lg:grid-cols-5">
      <Reveal className="lg:col-span-2">
        <div className="relative h-full min-h-[420px] overflow-hidden rounded-2xl border border-line shadow-sm" data-testid="contact-info-card">
          <RotatingImage images={CARD_IMAGES} interval={5000} alt={t.contact.imageAlt} className="absolute inset-0" />
          <div className="absolute inset-x-4 bottom-4 space-y-4 rounded-xl bg-slate-900/50 p-6 shadow-lg backdrop-blur-md">
            {infoItems.map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-3.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 text-white">
                  <Icon size={16} />
                </span>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">{label}</p>
                  <p className="mt-0.5 text-sm font-semibold text-white">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.12} className="lg:col-span-3">
        <form onSubmit={submit} className="rounded-2xl border border-line bg-white p-7 shadow-sm sm:p-9" data-testid="quote-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="nome">{t.contact.name}</label>
              <input id="nome" required minLength={2} value={form.nome} onChange={set("nome")} placeholder={t.contact.namePh} className={inputCls} data-testid="contact-name-input" />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="email">{t.contact.email}</label>
              <input id="email" type="email" required value={form.email} onChange={set("email")} placeholder={t.contact.emailPh} className={inputCls} data-testid="contact-email-input" />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="telefono">{t.contact.phone}</label>
              <input id="telefono" required minLength={6} value={form.telefono} onChange={set("telefono")} placeholder={t.contact.phonePh} className={inputCls} data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="tipo">{t.contact.workType}</label>
              <select id="tipo" value={form.tipo_lavoro} onChange={set("tipo_lavoro")} className={inputCls} data-testid="contact-worktype-select">
                {WORK_TYPE_VALUES.map((v, i) => (
                  <option key={v} value={v}>{t.contact.workTypes[i]}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="metratura">{t.contact.area}</label>
              <input id="metratura" type="number" min="1" value={form.metratura} onChange={set("metratura")} placeholder={t.contact.areaPh} className={inputCls} data-testid="contact-area-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="messaggio">{t.contact.message}</label>
              <textarea id="messaggio" required minLength={10} rows={5} value={form.messaggio} onChange={set("messaggio")} placeholder={t.contact.messagePh} className={`${inputCls} resize-none`} data-testid="contact-message-input" />
            </div>
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="quote-submit-button"
            className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-amber disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {sending ? <LoaderCircle size={17} className="animate-spin" /> : <Send size={16} />}
            {sending ? t.contact.sending : t.contact.submit}
          </button>
        </form>
      </Reveal>
    </div>
  );
}

export const ContactHeading = () => {
  const { t } = useLang();
  return (
    <SectionHeading
      overline={t.contact.overline}
      title={t.contact.title}
      desc={t.contact.desc}
    />
  );
};
